import { valibotResolver } from "@hookform/resolvers/valibot";
import { useState } from "react";
import { useForm } from "react-hook-form";

import { Button } from "common/components/ui/Button";
import { DialogTitle } from "common/components/ui/Dialog";
import { DrawerTitle } from "common/components/ui/Drawer";
import FormField from "common/components/ui/FormField";
import { Input } from "common/components/ui/Input";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import useWebhookEvents from "../../../datahooks/useWebhookEvents";
import useWebhookMutations from "../../../datahooks/useWebhookMutations";
import {
  ProtectedWebhookFormSchema,
  WebhookFormSchema,
  type WebhookFormType,
} from "../../../schema";
import WebhookEventsSelect from "../WebhookEventsSelect";
import WebhookHeader from "./WebhookHeader";

import type { Webhook } from "../../../types";

interface WebhookFormProps {
  webhook?: Webhook;
  onBack: () => void;
}

export default function WebhookForm({
  webhook = null,
  onBack,
}: WebhookFormProps) {
  const { headers, name, url, events, id } = webhook ?? {};

  const [isProtected, setIsProtected] = useState(webhook ? !!headers : false);
  const isDesktop = useTwBreakpoint("lg");

  const { updateWebhook, isUpdatingWebhook, createWebhook, isCreatingWebhook } =
    useWebhookMutations();
  const { webhookEvents, isLoadingWebhookEvents } = useWebhookEvents();

  let defaultValues: WebhookFormType;
  if (webhook) {
    defaultValues = {
      name,
      url,
      events,
      headers: {
        name: headers ? Object.keys(headers)[0] : "",
        value: headers ? Object.values(headers)[0] : "",
      },
    };
  } else {
    defaultValues = {
      name: "",
      url: "",
      events: [],
      headers: {
        name: "",
        value: "",
      },
    };
  }

  const {
    control,
    reset,
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<WebhookFormType>({
    defaultValues,
    resolver: valibotResolver(
      isProtected ? ProtectedWebhookFormSchema : WebhookFormSchema,
    ),
  });

  function onSubmit(data: WebhookFormType) {
    const submittedWebhook = {
      ...data,
      headers:
        isProtected && "headers" in data
          ? {
              [data.headers.name]: data.headers.value,
            }
          : null,
    };

    if (webhook) {
      updateWebhook({
        webhookId: id,
        updates: submittedWebhook,
      }).then(() => onBack());
    } else {
      createWebhook({
        newWebhook: submittedWebhook,
      }).then(() => onBack());
    }
  }

  const Title = isDesktop ? DialogTitle : DrawerTitle;

  return (
    <>
      <Title className="mb-8">{webhook ? "Edit" : "Add"} webhook</Title>

      {/* Name */}
      <FormField
        htmlFor="name"
        label="Name"
        error={errors.name?.message}
        className="mb-4"
      >
        <Input id="name" placeholder="Enter name" {...register("name")} />
      </FormField>

      <FormField
        label="URL"
        htmlFor="url"
        error={errors.url?.message}
        className="mb-4"
      >
        <Input id="url" placeholder="Paste URL" {...register("url")} />
      </FormField>

      {/* Optional Authorization headers */}
      <WebhookHeader
        control={control}
        isProtected={isProtected}
        setIsProtected={setIsProtected}
      />

      {/* Events */}
      <FormField
        label="Events"
        htmlFor="webhook-event-select"
        error={errors.events?.message}
      >
        <WebhookEventsSelect
          selection={watch("events")}
          setSelection={(newEvents) =>
            setValue("events", newEvents, {
              shouldValidate: true,
            })
          }
          events={webhookEvents}
          isLoading={isLoadingWebhookEvents}
          isMultiSelect
        />
      </FormField>

      <div className="mt-8 flex justify-between gap-x-2.5">
        <Button
          variant="secondary-black"
          disabled={isUpdatingWebhook || isCreatingWebhook}
          onClick={() => {
            onBack();
            reset();
          }}
        >
          Cancel
        </Button>

        <Button
          isLoading={isUpdatingWebhook || isCreatingWebhook}
          onClick={handleSubmit(onSubmit)}
        >
          Save
        </Button>
      </div>
    </>
  );
}
